import { useState, useMemo } from 'react';
import { APP } from './tokens';
import type { RightPanelSettings as ToolPanelSettings } from '../../types';
import { buildToolCategories, filterToolCategories } from './toolDefinitions';

// ─── Collapsed icon definitions ─────────────────────

interface CollapsedToolDef {
  label: string;
  icon: (stroke: string) => React.ReactNode;
}

const COLLAPSED_TOOLS: CollapsedToolDef[] = [
  {
    label: 'Document',
    icon: (stroke) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="8" y1="13" x2="16" y2="13" />
        <line x1="8" y1="17" x2="16" y2="17" />
      </svg>
    ),
  },
  {
    label: 'Notes',
    icon: (stroke) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <line x1="7" y1="8" x2="17" y2="8" />
        <line x1="7" y1="12" x2="17" y2="12" />
        <line x1="7" y1="16" x2="13" y2="16" />
      </svg>
    ),
  },
  {
    label: 'Image',
    icon: (stroke) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    ),
  },
  {
    label: 'Analytics',
    icon: (stroke) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <polyline points="22 12 18 12 15 20 9 4 6 12 2 12" />
      </svg>
    ),
  },
  {
    label: 'Signature',
    icon: (stroke) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
      </svg>
    ),
  },
  {
    label: 'Star',
    icon: (stroke) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
  },
  {
    label: 'Lock',
    icon: (stroke) => (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
  },
];

// ─── Props ──────────────────────────────────────────

export interface ToolPanelProps {
  expanded?: boolean;
  activeIndex?: number;
  onToolClick?: (index: number) => void;
  onExpand?: () => void;
  onCollapse?: () => void;
  onToolSelect?: (toolId: string, settings?: Record<string, unknown>) => void;
  settings?: ToolPanelSettings;
  onSettingsChange?: (patch: Partial<ToolPanelSettings>) => void;
}

// ─── Collapsed Mode ─────────────────────────────────

function CollapsedPanel({
  activeIndex = 0,
  onToolClick,
  onExpand,
}: {
  activeIndex?: number;
  onToolClick?: (index: number) => void;
  onExpand?: () => void;
}) {
  return (
    <div
      style={{
        width: 48,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        flexShrink: 0,
        paddingTop: 12,
        gap: 2,
        backgroundColor: APP.bgToolPanel,
        borderLeft: `1px solid ${APP.borderToolPanel}`,
        boxSizing: 'border-box',
      }}
    >
      <button
        type="button"
        onClick={onExpand}
        style={{
          width: 36, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0, marginBottom: 6, borderRadius: 6, border: 'none', background: 'none',
          cursor: 'pointer', padding: 0,
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={APP.strokeToolInactive} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      {COLLAPSED_TOOLS.map((tool, i) => {
        const isActive = i === activeIndex;
        return (
          <button
            key={tool.label}
            type="button"
            onClick={() => onToolClick?.(i)}
            title={tool.label}
            style={{
              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, borderRadius: 8,
              backgroundColor: isActive ? APP.bgToolActive : 'transparent',
              border: 'none', cursor: 'pointer', padding: 0,
            }}
          >
            {tool.icon(isActive ? APP.strokeToolActive : APP.strokeToolInactive)}
          </button>
        );
      })}
    </div>
  );
}

// ─── Expanded Mode ──────────────────────────────────

function ExpandedPanel({
  onCollapse,
  onToolSelect,
  settings = {},
  onSettingsChange,
}: {
  onCollapse?: () => void;
  onToolSelect?: (toolId: string, settings?: Record<string, unknown>) => void;
  settings?: ToolPanelSettings;
  onSettingsChange?: (patch: Partial<ToolPanelSettings>) => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedToolId, setExpandedToolId] = useState<string | null>(null);

  const allCategories = useMemo(() => buildToolCategories(), []);
  const filteredCategories = useMemo(
    () => filterToolCategories(allCategories, searchQuery),
    [allCategories, searchQuery],
  );

  const handleSettingsChange = (patch: Partial<ToolPanelSettings>) => {
    onSettingsChange?.(patch);
  };

  return (
    <div
      style={{
        width: 330,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        backgroundColor: APP.bgDocColumn,
        borderLeft: `1px solid ${APP.border}`,
        boxSizing: 'border-box',
        fontFamily: APP.fontFamily,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: 40,
          flexShrink: 0,
          paddingInline: 16,
          borderBottom: `1px solid ${APP.border}`,
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 600, color: APP.textPrimary }}>Tools</span>
        <button
          type="button"
          onClick={onCollapse}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 24, height: 24, border: 'none', background: 'none',
            cursor: 'pointer', padding: 0, borderRadius: 4,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={APP.strokeToolInactive} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="18" rx="1" />
            <path d="M14 9l3 3-3 3" />
          </svg>
        </button>
      </div>

      {/* Search Bar */}
      <div
        style={{
          flexShrink: 0,
          paddingBlock: 8,
          paddingInline: 12,
          borderBottom: `1px solid ${APP.border}`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            borderRadius: 7,
            paddingBlock: 6,
            paddingInline: 10,
            gap: 7,
            backgroundColor: APP.bgToolSearch,
            border: `1px solid ${APP.borderQuickAction}`,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={APP.textToolSearchPlaceholder} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search tools..."
            autoComplete="off"
            spellCheck={false}
            style={{
              flex: 1, border: 'none', background: 'none', outline: 'none',
              fontSize: 13, fontFamily: APP.fontFamily, color: APP.textPrimary,
              padding: 0, lineHeight: '16px',
            }}
          />
        </div>
      </div>

      {/* Scrollable Tool List */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        {filteredCategories.map((cat) => (
          <div key={cat.id}>
            {/* Category Header */}
            <div
              style={{
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: '0.1em',
                color: APP.textToolCategory,
                textTransform: 'uppercase' as const,
                paddingTop: 16,
                paddingBottom: 6,
                paddingInline: 16,
                fontFamily: APP.fontFamily,
              }}
            >
              {cat.label}
            </div>

            {/* Tool Rows */}
            {cat.tools.map((tool) => {
              const isExpanded = expandedToolId === tool.id;
              return (
                <div key={tool.id}>
                  {/* Tool Row */}
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingBlock: 6,
                      paddingInline: 16,
                      gap: 9,
                      cursor: 'pointer',
                      backgroundColor: isExpanded ? APP.bgToolRowExpanded : 'transparent',
                      transition: 'background 100ms ease',
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) e.currentTarget.style.backgroundColor = APP.bgToolRowExpanded;
                    }}
                    onMouseLeave={(e) => {
                      if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* Click area: icon + label → runs the tool */}
                    <div
                      style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 9 }}
                      onClick={() => onToolSelect?.(tool.id)}
                    >
                      {tool.icon(APP.strokeToolActive)}
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: '16px',
                          color: APP.textPrimary,
                          flex: 1,
                          fontFamily: APP.fontFamily,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {tool.label}
                      </span>
                    </div>

                    {/* Settings chevron */}
                    {tool.hasSettings && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setExpandedToolId(isExpanded ? null : tool.id);
                        }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: APP.textMuted, padding: 4,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <svg
                          width="11" height="11" viewBox="0 0 24 24" fill="none"
                          stroke={APP.textMuted} strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round"
                          style={{
                            transform: isExpanded ? 'rotate(180deg)' : 'none',
                            transition: 'transform 150ms ease',
                          }}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Expanded Settings */}
                  {tool.hasSettings && isExpanded && tool.renderSettings && (
                    <div
                      style={{
                        paddingTop: 8,
                        paddingRight: 16,
                        paddingBottom: 10,
                        paddingLeft: 38,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                        backgroundColor: APP.bgToolSettingsPanel,
                        borderBottom: `1px solid ${APP.borderToolSettings}`,
                      }}
                    >
                      {tool.renderSettings({ settings, onSettingsChange: handleSettingsChange })}

                      {/* Run button */}
                      <button
                        type="button"
                        onClick={() => onToolSelect?.(tool.id, settings as Record<string, unknown>)}
                        style={{
                          width: '100%',
                          marginTop: 4,
                          padding: '6px 0',
                          fontSize: 12,
                          fontFamily: APP.fontFamily,
                          fontWeight: 500,
                          color: '#FFFFFF',
                          background: APP.textPrimary,
                          border: 'none',
                          borderRadius: 6,
                          cursor: 'pointer',
                        }}
                      >
                        Run {tool.label}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Main Export ─────────────────────────────────────

export default function ToolPanel({
  expanded = false,
  activeIndex = 0,
  onToolClick,
  onExpand,
  onCollapse,
  onToolSelect,
  settings = {},
  onSettingsChange,
}: ToolPanelProps) {
  if (expanded) {
    return (
      <ExpandedPanel
        onCollapse={onCollapse}
        onToolSelect={onToolSelect}
        settings={settings}
        onSettingsChange={onSettingsChange}
      />
    );
  }

  return (
    <CollapsedPanel
      activeIndex={activeIndex}
      onToolClick={onToolClick}
      onExpand={onExpand}
    />
  );
}
